import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';

const filters = [
  { icon: 'ri-global-line', label: 'Tüm Ülkeler' },
  { icon: 'ri-user-line', label: 'Tüm Cinsiyetler' },
  { icon: 'ri-translate-2', label: 'Türkçe' },
];

const MatchingPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'searching' | 'found' | 'cancelled'>('searching');
  const [dots, setDots] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    const elapsedInterval = setInterval(() => setElapsed((p) => p + 1), 1000);
    // Simulate finding a match after 4 seconds
    const foundTimeout = setTimeout(() => setStatus('found'), 4000);

    return () => {
      clearInterval(dotsInterval);
      clearInterval(elapsedInterval);
      clearTimeout(foundTimeout);
    };
  }, []);

  useEffect(() => {
    if (status === 'found') {
      const t = setTimeout(() => navigate('/chat'), 2500);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  const handleCancel = () => {
    setStatus('cancelled');
  };

  return (
    <div className="min-h-screen bg-[#05070d] flex flex-col">
      <Header />

      {/* BG animated orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-cyan-500/6 blur-[130px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-violet-600/8 blur-[110px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'linear-gradient(#ffffff 1px,transparent 1px),linear-gradient(90deg,#ffffff 1px,transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-24">
        {/* Main card */}
        <div className="relative w-full max-w-lg">
          <div className="absolute -inset-px rounded-3xl bg-gradient-to-r from-cyan-500/20 via-violet-600/10 to-cyan-500/20 blur-sm" />
          <div className="relative bg-[#0d1117]/90 backdrop-blur-xl rounded-3xl border border-white/10 p-10 text-center">

            {status === 'searching' && (
              <>
                {/* Animated spinner rings */}
                <div className="relative w-36 h-36 mx-auto mb-8 flex items-center justify-center">
                  {/* Outer ring */}
                  <div className="absolute inset-0 rounded-full border-2 border-cyan-500/20 animate-spin" style={{ animationDuration: '3s' }} />
                  <div className="absolute inset-2 rounded-full border-2 border-violet-500/15 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
                  <div className="absolute inset-4 rounded-full border-2 border-cyan-400/10 animate-spin" style={{ animationDuration: '1.5s' }} />
                  {/* Pulsing center */}
                  <div className="absolute inset-6 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-600/20 animate-pulse" />
                  <div className="relative z-10 flex flex-col items-center">
                    <i className="ri-user-search-line text-cyan-400 text-3xl" />
                  </div>
                  {/* Orbit dots */}
                  {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full bg-cyan-400"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${deg}deg) translateX(60px) translateY(-50%)`,
                        opacity: i % 2 === 0 ? 0.6 : 0.2,
                        animation: `spin 3s linear infinite`,
                      }}
                    />
                  ))}
                </div>

                <h2 className="text-white font-extrabold text-2xl mb-2">
                  Sana uygun biri aranıyor{dots}
                </h2>
                <p className="text-white/40 text-sm mb-2">
                  Dünya genelindeki aktif kullanıcılar taranıyor
                </p>
                <p className="text-white/25 text-xs mb-8 font-mono">
                  {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
                </p>

                {/* Live counter */}
                <div className="flex items-center justify-center gap-2 mb-8 px-4 py-2.5 rounded-full bg-emerald-500/8 border border-emerald-500/20 w-fit mx-auto">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  <span className="text-emerald-400 text-sm font-medium">14.328 kişi şu an çevrimiçi</span>
                </div>

                {/* Active filters */}
                <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
                  {filters.map((f) => (
                    <div
                      key={f.label}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/50 text-xs"
                    >
                      <i className={`${f.icon} text-xs`} />
                      {f.label}
                    </div>
                  ))}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 text-xs hover:bg-cyan-500/20 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-equalizer-line text-xs" />
                    Filtrele
                  </button>
                </div>

                {/* Filter panel */}
                {showFilters && (
                  <div className="mb-8 p-5 rounded-2xl bg-white/3 border border-white/8 text-left">
                    <h4 className="text-white/70 text-sm font-semibold mb-4">Filtreler</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/40 text-xs mb-2">Cinsiyet</label>
                        <select className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/50 cursor-pointer">
                          <option value="all">Tümü</option>
                          <option value="male">Erkek</option>
                          <option value="female">Kadın</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/40 text-xs mb-2">Ülke</label>
                        <select className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/50 cursor-pointer">
                          <option value="all">Tüm Ülkeler</option>
                          <option value="tr">🇹🇷 Türkiye</option>
                          <option value="de">🇩🇪 Almanya</option>
                          <option value="us">🇺🇸 ABD</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-white/25 text-xs mt-3">
                      <i className="ri-vip-crown-line text-yellow-400" /> Cinsiyet ve ülke filtreleri Premium özelliktir.
                    </p>
                  </div>
                )}

                <button
                  onClick={handleCancel}
                  className="w-full py-3.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm font-medium hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-stop-circle-line mr-2" />
                  İptal Et
                </button>
              </>
            )}

            {status === 'found' && (
              <div className="flex flex-col items-center">
                <div className="relative w-28 h-28 mb-6 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" style={{ animationDuration: '1s' }} />
                  <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-white text-4xl" />
                  </div>
                </div>
                <h2 className="text-white font-extrabold text-2xl mb-2">Eşleşme Bulundu!</h2>
                <p className="text-white/50 text-sm mb-6">Bağlantı kuruluyor...</p>
                <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-sm">
                  <i className="ri-loader-4-line animate-spin" />
                  Video başlatılıyor...
                </div>
              </div>
            )}

            {status === 'cancelled' && (
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
                  <i className="ri-close-circle-line text-white/30 text-4xl" />
                </div>
                <h2 className="text-white font-extrabold text-2xl mb-2">Arama İptal Edildi</h2>
                <p className="text-white/45 text-sm mb-8">İstediğin zaman tekrar başlayabilirsin.</p>
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button
                    onClick={() => { setStatus('searching'); setElapsed(0); }}
                    className="flex-1 py-3.5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-sm cursor-pointer hover:from-cyan-400 hover:to-violet-500 transition-all whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2" />
                    Yeniden Ara
                  </button>
                  <Link
                    to="/"
                    className="flex-1 py-3.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm font-medium text-center hover:bg-white/10 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                  >
                    Ana Sayfaya Dön
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        {status === 'searching' && (
          <div className="mt-8 max-w-lg w-full">
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: 'ri-lightbulb-line', text: 'İyi aydınlatma bağlantı kalitesini artırır' },
                { icon: 'ri-headphone-line', text: 'Kulaklık kullanmak eko engeller' },
                { icon: 'ri-wifi-line', text: 'Kararlı bağlantı için WiFi tercih et' },
              ].map((tip) => (
                <div key={tip.text} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/2 border border-white/6 text-center">
                  <i className={`${tip.icon} text-white/30 text-lg`} />
                  <p className="text-white/30 text-xs leading-relaxed">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingPage;
